import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Button,
  Input,
  DatePicker,
  Form,
  Tag,
  Space,
  Typography,
  Tooltip,
  App,
  Divider,
  Modal,
  Checkbox,
} from 'antd';
import {
  SearchOutlined,
  ClearOutlined,
  FilterOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ProfileOutlined,
  WarningOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listCompetitions,
  listCompetitionsForDownload,
} from '../../services/competitions';
import {
  listModalities,
  listPeriods,
  listCompetitionRegions,
  listCitiesByState,
} from '../../services/catalogs';
import { getRegionById, getRegionStatesForDownload } from '../../services/regions';
import {
  getCompetitionVideos,
  getDownloadableCompetitionVideos,
  getCompetitionVideoReports,
  getVideoReportDetail,
  getCommentReportDetail,
  applyVideoModerationAction,
  applyCommentModerationAction,
  getScoresById,
  getHiddenUsers,
  unbanClient,
} from '../../services/reports';
import { getEvaluationById } from '../../services/evaluations';
import { resolveMedia } from '../../utils/media';
import usePermissions from '../../hooks/usePermissions';
import ReportDetailContent from './components/ReportDetailContent';
import HiddenUsersModal, {
  readExplicitPendingCount,
} from './components/HiddenUsersModal';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const FINALIST_OPTIONS = [
  { value: 'finalistas', label: 'Finalista' },
  { value: 'no-finalistas', label: 'No finalista' },
];

const STATUS_OPTIONS = [
  { value: 'En Curso', label: 'En Curso' },
  { value: 'Terminada', label: 'Terminada' },
];

/** Valores que acepta el API en query param isFinalist */
function normalizeIsFinalistForApi(raw) {
  if (raw == null || raw === '') return undefined;
  const s = String(raw).trim();
  if (s === 'finalistas' || s === 'Finalista' || s === 'finalista') return 'finalistas';
  if (
    s === 'no-finalistas' ||
    s === 'No finalista' ||
    s === 'no finalista' ||
    s === 'no-finalista'
  ) {
    return 'no-finalistas';
  }
  return s;
}

/** True si el usuario eligió al menos un criterio de búsqueda (alineado con buildParams). */
function hasAtLeastOneFilter(values) {
  if (!values || typeof values !== 'object') return false;
  const id = (v) => v != null && v !== '';
  if (id(values.competitionId)) return true;
  if (id(values.modalityId)) return true;
  if (id(values.periodId)) return true;
  if (normalizeIsFinalistForApi(values.isFinalist)) return true;
  if (id(values.regionId)) return true;
  if (id(values.stateId)) return true;
  if (id(values.cityId)) return true;
  if (String(values.participantSearch || '').trim()) return true;
  if (values.dateRange?.[0] && values.dateRange?.[1]) return true;
  if (id(values.statusCompetition)) return true;
  if (values.onlyReported === true) return true;
  return false;
}

function formatUserFullName(user) {
  if (!user || typeof user !== 'object') return null;
  const fullName = [user.firstName, user.lastName, user.secondLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || null;
}

function formatReporterName(reportedBy) {
  if (!reportedBy || typeof reportedBy !== 'object') return '-';
  return (
    formatUserFullName(reportedBy) ||
    reportedBy.nickname ||
    reportedBy.email ||
    '-'
  );
}

function getTotalReportCount(record) {
  if (record?.totalReportCount != null && record.totalReportCount !== '') {
    return Number(record.totalReportCount) || 0;
  }
  return (
    (Number(record?.videoReportCount) || 0) +
    (Number(record?.commentReportCount) || 0)
  );
}

function reportSelectionKey(contentType, id) {
  const type = contentType === 'COMMENT' ? 'COMMENT' : 'VIDEO';
  return `${type}:${id}`;
}

function parseReportSelectionKey(key) {
  if (typeof key !== 'string' || !key.includes(':')) {
    return { contentType: 'VIDEO', id: key };
  }
  const separator = key.indexOf(':');
  const type = key.slice(0, separator);
  const id = key.slice(separator + 1);
  return {
    contentType: type === 'COMMENT' ? 'COMMENT' : 'VIDEO',
    id,
  };
}

function mapCompetitionVideoReportsPayload(result) {
  return {
    video: result?.video ?? null,
    reports: result?.reports ?? [],
    total: result?.total ?? result?.reports?.length ?? 0,
    commentReports: result?.commentReports ?? [],
    commentReportTotal:
      result?.commentReportTotal ?? result?.commentReports?.length ?? 0,
  };
}

function contentTypeTag(contentType) {
  if (contentType === 'COMMENT') {
    return (
      <Tag icon={<CommentOutlined />} color="default">
        Comentario
      </Tag>
    );
  }
  return (
    <Tag icon={<VideoCameraOutlined />} color="default">
      Video
    </Tag>
  );
}

function formatCompetitionLabel(competition) {
  if (competition == null || competition === '') return '-';
  if (typeof competition === 'string' || typeof competition === 'number') {
    return String(competition);
  }
  return competition.description || competition.name || '-';
}

const REPORT_STATUS_MAP = {
  PENDING: { label: 'Pendiente', color: 'warning' },
  REVIEWING: { label: 'En revisión', color: 'processing' },
  RESOLVED: { label: 'Resuelto', color: 'success' },
  DISMISSED: { label: 'Desestimado', color: 'default' },
};

function reportStatusTag(status) {
  const mapped = REPORT_STATUS_MAP[status] ?? {
    label: status || '-',
    color: 'default',
  };
  return <Tag color={mapped.color}>{mapped.label}</Tag>;
}

const MODERATION_CONFIRM = {
  DELETE_VIDEO: {
    title: '¿Eliminar este video?',
    content:
      'El video dejará de estar disponible para los usuarios. Esta acción de moderación no bloqueará al usuario.',
    okText: 'Eliminar video',
    danger: true,
    success: 'Video eliminado correctamente.',
  },
  BLOCK_USER: {
    title: '¿Bloquear este usuario?',
    content:
      'El usuario no podrá volver a acceder a la aplicación. El video no será eliminado automáticamente.',
    okText: 'Bloquear usuario',
    danger: true,
    success: 'Usuario bloqueado correctamente.',
  },
  NO_ACTION: {
    title: '¿No aplicar ninguna acción?',
    content:
      'El reporte será descartado y el contenido continuará disponible.',
    okText: 'Confirmar',
    danger: false,
    success: 'Reporte descartado correctamente.',
  },
};

const COMMENT_MODERATION_CONFIRM = {
  DELETE_COMMENT: {
    title: '¿Eliminar comentario?',
    content: 'El comentario dejará de estar disponible para los usuarios.',
    okText: 'Sí, eliminar comentario',
    danger: true,
    success: 'El comentario fue eliminado correctamente.',
  },
  BLOCK_USER: {
    title: '¿Bloquear usuario?',
    content:
      'Esta acción impedirá que el usuario vuelva a ingresar a la aplicación.',
    okText: 'Sí, bloquear usuario',
    danger: true,
    success: 'El usuario fue bloqueado correctamente.',
  },
  DELETE_COMMENT_AND_BLOCK_USER: {
    title: '¿Eliminar comentario y bloquear usuario?',
    content:
      'El comentario dejará de estar disponible y el usuario no podrá volver a ingresar a la aplicación.',
    okText: 'Sí, eliminar y bloquear',
    danger: true,
    success:
      'El comentario fue eliminado y el usuario fue bloqueado correctamente.',
  },
  NO_ACTION: {
    title: '¿No aplicar ninguna acción?',
    content:
      'El reporte se marcará como revisado y el comentario permanecerá disponible.',
    okText: 'Sí, no aplicar acción',
    danger: false,
    success: 'El reporte fue marcado como revisado sin aplicar acciones.',
  },
};

function isUsableBackendMessage(msg) {
  if (typeof msg !== 'string') return false;
  const trimmed = msg.trim();
  if (!trimmed || trimmed.length > 200) return false;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) return false;
  if (trimmed.includes('\n') || trimmed.toLowerCase().includes('stack')) {
    return false;
  }
  return true;
}

function getModerationErrorMessage(err, contentType) {
  const isComment = contentType === 'COMMENT';
  const status = err?.__normalized?.status ?? err?.response?.status;
  const code = String(err?.__normalized?.code ?? '').toUpperCase();
  if (status === 403) {
    return 'No tienes permiso para realizar esta acción.';
  }
  if (status === 404) {
    return isComment
      ? 'El reporte de comentario ya no está disponible.'
      : 'El reporte ya no está disponible.';
  }
  if (status === 409 && code === 'INCOMPATIBLE_MODERATION_ACTION') {
    return isComment
      ? 'Este reporte ya fue atendido y no permite aplicar esta acción.'
      : 'El reporte ya fue atendido y no permite realizar esta acción.';
  }
  if (status === 409 && code === 'COMMENT_OWNER_NOT_AVAILABLE') {
    return 'No fue posible identificar al usuario responsable del comentario.';
  }
  if (status === 409 && code === 'VIDEO_OWNER_NOT_AVAILABLE') {
    return 'No fue posible identificar al usuario responsable del video.';
  }
  if (status === 400 && isUsableBackendMessage(err?.__normalized?.message)) {
    return err.__normalized.message.trim();
  }
  return isComment
    ? 'No fue posible aplicar la acción de moderación. Intenta nuevamente.'
    : 'Ha ocurrido un error al aplicar la acción de moderación.';
}

function getUnbanErrorMessage(err) {
  const status = err?.__normalized?.status ?? err?.response?.status;
  if (status === 403) {
    return 'No tienes permisos para realizar esta acción.';
  }
  if (status === 404) {
    return 'El usuario ya no está disponible.';
  }
  return 'No se pudo desbanear al usuario. Inténtalo nuevamente.';
}

function fileExtensionFromUrl(url) {
  try {
    const pathname = new URL(url, window.location.href).pathname;
    const match = pathname.match(/\.([a-zA-Z0-9]{1,8})$/);
    return match ? `.${match[1].toLowerCase()}` : '.mp4';
  } catch {
    return '.mp4';
  }
}

function safeVideoFilename(title, ext) {
  const dotExt = ext.startsWith('.') ? ext : `.${ext}`;
  const base = String(title || 'video')
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120) || 'video';
  return `${base}${dotExt}`;
}

const PAGE_SIZE_OPTIONS = ['10', '20', '50'];

const CompetitionVideosPage = () => {
  const { message, modal } = App.useApp();
  const { can } = usePermissions();
  const canModerate = can('moderation.act');
  const canViewCompetitions = can('competitions.view');
  const useDownloadEndpoints =
    can('competitions.download') && !canViewCompetitions;
  const canViewScores =
    can('competitions_eval.history') || can('competitions_eval.top');
  const canViewAiVerdict = can('competitions_eval.view');

  const [form] = Form.useForm();
  const regionId = Form.useWatch('regionId', form);
  const stateId = Form.useWatch('stateId', form);
  const [filterFormRevision, bumpFilterFormRevision] = useReducer(
    (x) => x + 1,
    0
  );

  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [sorter, setSorter] = useState({});

  const [competitions, setCompetitions] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [regions, setRegions] = useState([]);
  const [statesInRegion, setStatesInRegion] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const [loadingStatesByRegion, setLoadingStatesByRegion] = useState(false);

  const [activeFilters, setActiveFilters] = useState(null);
  const [downloadingUrl, setDownloadingUrl] = useState(null);
  /** { url, title } | null — vista previa en modal */
  const [videoPreview, setVideoPreview] = useState(null);

  const [loadingRowKey, setLoadingRowKey] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [loadingIA, setLoadingIA] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  /** { video, reports, total, commentReports, commentReportTotal } | null */
  const [reportsDetail, setReportsDetail] = useState(null);
  const [loadingReportsVideoId, setLoadingReportsVideoId] = useState(null);
  const [loadingReportKey, setLoadingReportKey] = useState(null);
  const [selectedReportDetail, setSelectedReportDetail] = useState(null);
  const [selectedReportKey, setSelectedReportKey] = useState(null);
  const [applyingModerationAction, setApplyingModerationAction] = useState(false);
  const [unbanningUser, setUnbanningUser] = useState(false);
  const unbanInFlightRef = useRef(false);
  const [closeReportsModalOnBack, setCloseReportsModalOnBack] = useState(false);
  const [hiddenUsersOpen, setHiddenUsersOpen] = useState(false);
  const [hiddenUsersPendingCount, setHiddenUsersPendingCount] = useState(null);


  useEffect(() => {
    if (!canViewCompetitions) {
      setHiddenUsersPendingCount(null);
      return undefined;
    }
    let cancelled = false;
    getHiddenUsers({ page: 1, pageSize: 1 })
      .then((result) => {
        if (cancelled) return;
        setHiddenUsersPendingCount(readExplicitPendingCount(result));
      })
      .catch(() => {
        if (!cancelled) setHiddenUsersPendingCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [canViewCompetitions]);

  useEffect(() => {
    let cancelled = false;
    setLoadingCatalogs(true);
    const loadCompetitions = useDownloadEndpoints
      ? listCompetitionsForDownload({ pageSize: 200 })
      : listCompetitions({ pageSize: 200 });

    Promise.allSettled([
      loadCompetitions,
      listModalities(),
      listPeriods(),
      listCompetitionRegions(),
    ])
      .then(([compResult, mods, pds, regs]) => {
        if (cancelled) return;
        if (compResult.status === 'fulfilled') {
          const value = compResult.value;
          setCompetitions(value?.items ?? value ?? []);
        } else {
          setCompetitions([]);
        }
        setModalities(mods.status === 'fulfilled' ? mods.value ?? [] : []);
        setPeriods(pds.status === 'fulfilled' ? pds.value ?? [] : []);
        setRegions(regs.status === 'fulfilled' ? regs.value ?? [] : []);
        const failed = [compResult, mods, pds, regs].filter(
          (result) => result.status === 'rejected',
        ).length;
        if (failed === 4) {
          message.error('Error al cargar los catálogos');
        } else if (failed > 0) {
          message.error('Algunos catálogos no se pudieron cargar');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCatalogs(false);
      });

    return () => {
      cancelled = true;
    };
  }, [message, useDownloadEndpoints]);

  useEffect(() => {
    if (!regionId) {
      setStatesInRegion([]);
      setCities([]);
      form.setFieldValue('stateId', undefined);
      form.setFieldValue('cityId', undefined);
      return;
    }

    let cancelled = false;
    setLoadingStatesByRegion(true);
    const loadRegionStates = useDownloadEndpoints
      ? getRegionStatesForDownload(regionId).then((payload) => ({
          regionStates: (payload?.states ?? []).map((state) => ({ state })),
        }))
      : getRegionById(regionId);

    loadRegionStates
      .then((region) => {
        if (cancelled) return;
        const list = (region?.regionStates ?? [])
          .map((rs) => rs?.state)
          .filter(Boolean);
        setStatesInRegion(list);
        form.setFieldValue('stateId', undefined);
        form.setFieldValue('cityId', undefined);
      })
      .catch(() => {
        if (!cancelled) {
          message.error('No se pudieron cargar los estados de la región');
          setStatesInRegion([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingStatesByRegion(false);
      });

    return () => {
      cancelled = true;
    };
  }, [regionId, form, message, useDownloadEndpoints]);

  useEffect(() => {
    if (stateId) {
      listCitiesByState(stateId).then((data) => setCities(data ?? []));
    } else {
      setCities([]);
      form.setFieldValue('cityId', undefined);
    }
  }, [stateId, form]);

  const buildParams = useCallback(
    (page, pageSize, srt) => {
      const values = form.getFieldsValue();
      const params = {
        page: page ?? pagination.current,
        pageSize: pageSize ?? pagination.pageSize,
      };
      if (values.competitionId) params.competitionId = values.competitionId;
      if (values.modalityId) params.modalityId = values.modalityId;
      if (values.periodId) params.periodId = values.periodId;
      const isFinalist = normalizeIsFinalistForApi(values.isFinalist);
      if (isFinalist) params.isFinalist = isFinalist;
      if (values.regionId) params.regionId = values.regionId;
      if (values.stateId) params.stateId = values.stateId;
      if (values.cityId) params.cityId = values.cityId;
      if (values.participantSearch) params.participantSearch = values.participantSearch;
      if (values.dateRange?.[0]) params.startDate = values.dateRange[0].toISOString();
      if (values.dateRange?.[1]) params.endDate = values.dateRange[1].toISOString();
      if (values.statusCompetition) params.statusCompetition = values.statusCompetition;
      if (canViewCompetitions && values.onlyReported === true) params.onlyReported = true;
      const activeSorter = srt ?? sorter;
      if (activeSorter?.field === 'position' && activeSorter?.order) {
        params.sortField = 'position';
        params.sortOrder = activeSorter.order === 'ascend' ? 'asc' : 'desc';
      }
      return params;
    },
    [form, pagination, sorter, canViewCompetitions]
  );

  const fetchVideos = useCallback(
    async (page, pageSize, srt) => {
      const values = form.getFieldsValue();
      if (!hasAtLeastOneFilter(values)) return;

      const params = buildParams(page, pageSize, srt);

      setLoading(true);
      try {
        const result = useDownloadEndpoints
          ? await getDownloadableCompetitionVideos(params)
          : await getCompetitionVideos(params);
        setVideos(result?.items ?? []);
        setTotal(result?.total ?? 0);
      } catch {
        message.error('Error al cargar los videos');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildParams, form, useDownloadEndpoints]
  );

  const handleApplyFilters = () => {
    const values = form.getFieldsValue();
    if (!hasAtLeastOneFilter(values)) {
      message.warning('Selecciona al menos un filtro para buscar');
      return;
    }
    const newPage = 1;
    setPagination((prev) => ({ ...prev, current: newPage }));
    setActiveFilters(values);
    fetchVideos(newPage, pagination.pageSize, sorter);
  };

  const handleClearFilters = () => {
    form.resetFields();
    setVideos([]);
    setTotal(0);
    setActiveFilters(null);
    setPagination({ current: 1, pageSize: 10 });
    setSorter({});
  };

  const handleTableChange = (pag, _filters, srt) => {
    setPagination({ current: pag.current, pageSize: pag.pageSize });
    setSorter(srt || {});
    fetchVideos(pag.current, pag.pageSize, srt);
  };

  const handleDownload = useCallback(
    async (record) => {
      if (!record.urlVideo) {
        message.warning('Este video no tiene URL disponible');
        return;
      }
  
      const url = record.urlVideo;
      const ext = fileExtensionFromUrl(url);
      const filename = safeVideoFilename(record.title, ext);
  
      setDownloadingUrl(url);
      const hideLoading = message.loading('Descargando video…', 0);
  
      try {
        const res = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
        });
  
        if (!res.ok) {
          throw new Error(`fetch failed: ${res.status}`);
        }
  
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
  
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
  
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  
        message.success('Descarga iniciada');
      } catch (error) {
        console.error('Error al descargar video:', error);
        message.error(
          'No se pudo descargar el video. El origen del archivo debe permitir CORS para esta aplicación.'
        );
      } finally {
        hideLoading();
        setDownloadingUrl(null);
      }
    },
    [message]
  );

  /*const handleDownload = useCallback(
    async (record) => {
      if (!record.urlVideo) {
        message.warning('Este video no tiene URL disponible');
        return;
      }
      const url = record.urlVideo;
      const ext = fileExtensionFromUrl(url);
      const filename = safeVideoFilename(record.title, ext);
      setDownloadingUrl(url);
      const hideLoading = message.loading('Descargando video…', 0);
      try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) throw new Error('fetch failed');
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        message.success('Descarga iniciada');
      } catch {
        message.error(
          'No se pudo descargar el video. Si la URL es de otro dominio, el almacén debe permitir CORS para esta app.'
        );
      } finally {
        hideLoading();
        setDownloadingUrl(null);
      }
    },
    [message]
  );*/

  const openVideoPreview = useCallback(
    (record) => {
      if (!record.urlVideo) {
        message.warning('Este video no tiene URL disponible');
        return;
      }
      setVideoPreview({
        url: record.urlVideo,
        title: record.title || 'Vista previa',
      });
    },
    [message]
  );

  const closeReportsModal = useCallback(() => {
    setReportsDetail(null);
    setSelectedReportDetail(null);
    setSelectedReportKey(null);
    setLoadingReportKey(null);
    setApplyingModerationAction(false);
    unbanInFlightRef.current = false;
    setUnbanningUser(false);
    setCloseReportsModalOnBack(false);
  }, []);

  const backToReportsList = useCallback(() => {
    if (closeReportsModalOnBack) {
      closeReportsModal();
      return;
    }
    setSelectedReportDetail(null);
    setSelectedReportKey(null);
  }, [closeReportsModalOnBack, closeReportsModal]);

  const openVideoReports = useCallback(
    async (record) => {
      if (!canViewCompetitions) return;
      if (!record?.id) {
        message.warning('Este video no tiene identificador válido');
        return;
      }
      if (loadingReportsVideoId != null) return;

      setLoadingReportsVideoId(record.id);
      setSelectedReportDetail(null);
      setSelectedReportKey(null);
      setCloseReportsModalOnBack(false);
      try {
        const result = await getCompetitionVideoReports(record.id);
        setReportsDetail(mapCompetitionVideoReportsPayload(result));
      } catch {
        message.error('No fue posible consultar los reportes del video.');
      } finally {
        setLoadingReportsVideoId(null);
      }
    },
    [message, loadingReportsVideoId, canViewCompetitions]
  );

  const openReportDetail = useCallback(
    async (reportId, contentType) => {
      if (reportId == null) return;
      if (loadingReportKey != null) return;

      const type = contentType === 'COMMENT' ? 'COMMENT' : 'VIDEO';
      const key = reportSelectionKey(type, reportId);
      setLoadingReportKey(key);
      try {
        const result =
          type === 'COMMENT'
            ? await getCommentReportDetail(reportId)
            : await getVideoReportDetail(reportId);
        setSelectedReportKey(key);
        setSelectedReportDetail({
          ...result,
          contentType:
            type === 'COMMENT'
              ? 'COMMENT'
              : result?.contentType || 'VIDEO',
        });
      } catch (err) {
        const status = err?.__normalized?.status ?? err?.response?.status;
        if (status === 404) {
          message.error(
            type === 'COMMENT'
              ? 'El reporte de comentario ya no está disponible.'
              : 'El reporte ya no se encuentra disponible.'
          );
        } else {
          message.error('No fue posible consultar el detalle del reporte.');
        }
      } finally {
        setLoadingReportKey(null);
      }
    },
    [message, loadingReportKey]
  );

  const refreshReportDetail = useCallback(async (reportId, contentType) => {
    if (reportId == null) return;
    const type = contentType === 'COMMENT' ? 'COMMENT' : 'VIDEO';
    const result =
      type === 'COMMENT'
        ? await getCommentReportDetail(reportId)
        : await getVideoReportDetail(reportId);
    setSelectedReportKey(reportSelectionKey(type, reportId));
    setSelectedReportDetail({
      ...result,
      contentType:
        type === 'COMMENT' ? 'COMMENT' : result?.contentType || 'VIDEO',
    });
  }, []);

  const refreshReportsListForVideo = useCallback(async (videoId) => {
    if (videoId == null) return;
    const result = await getCompetitionVideoReports(videoId);
    setReportsDetail(mapCompetitionVideoReportsPayload(result));
  }, []);

  const applyModerationAction = useCallback(
    async (action) => {
      const contentType =
        selectedReportDetail?.contentType === 'COMMENT' ? 'COMMENT' : 'VIDEO';
      const parsed = parseReportSelectionKey(selectedReportKey);

      if (contentType === 'VIDEO' && parsed.contentType === 'COMMENT') return;
      if (contentType === 'COMMENT' && parsed.contentType === 'VIDEO') return;

      const reportId =
        parsed.id ??
        selectedReportDetail?.report?.id ??
        selectedReportDetail?.id;
      if (reportId == null || applyingModerationAction) return;

      const videoId =
        selectedReportDetail?.video?.id ?? reportsDetail?.video?.id ?? null;

      setApplyingModerationAction(true);
      try {
        const data =
          contentType === 'COMMENT'
            ? await applyCommentModerationAction(reportId, action)
            : await applyVideoModerationAction(reportId, action);

        const applyStatusFromPost = () => {
          if (!data?.reportStatus) return;
          setSelectedReportDetail((prev) =>
            prev
              ? {
                  ...prev,
                  report: {
                    ...(prev.report || {}),
                    status: data.reportStatus,
                  },
                }
              : prev
          );
        };

        if (contentType === 'VIDEO' && action === 'DELETE_VIDEO') {
          setCloseReportsModalOnBack(true);
          await fetchVideos(pagination.current, pagination.pageSize);
          try {
            await refreshReportDetail(reportId, 'VIDEO');
          } catch {
            applyStatusFromPost();
          }
          message.success(MODERATION_CONFIRM.DELETE_VIDEO.success);
          return;
        }

        try {
          await refreshReportDetail(reportId, contentType);
        } catch {
          applyStatusFromPost();
        }
        if (videoId != null) {
          try {
            await refreshReportsListForVideo(videoId);
          } catch {
            // La lista se actualizará al reabrir el modal.
          }
        }
        if (contentType === 'COMMENT') {
          await fetchVideos(pagination.current, pagination.pageSize);
        }
        const successMessage =
          contentType === 'COMMENT'
            ? COMMENT_MODERATION_CONFIRM[action]?.success
            : MODERATION_CONFIRM[action]?.success;
        message.success(successMessage);
      } catch (err) {
        message.error(getModerationErrorMessage(err, contentType));
        const status = err?.__normalized?.status ?? err?.response?.status;
        const code = String(err?.__normalized?.code ?? '').toUpperCase();
        if (status === 409) {
          try {
            await refreshReportDetail(reportId, contentType);
          } catch {
            // Si el refetch falla, el mensaje de 409 ya se mostró.
          }
          if (
            contentType === 'COMMENT' &&
            code === 'INCOMPATIBLE_MODERATION_ACTION' &&
            videoId != null
          ) {
            try {
              await refreshReportsListForVideo(videoId);
            } catch {
              // El listado se actualizará al reabrir el modal.
            }
          }
        }
      } finally {
        setApplyingModerationAction(false);
      }
    },
    [
      applyingModerationAction,
      fetchVideos,
      message,
      pagination,
      refreshReportDetail,
      refreshReportsListForVideo,
      reportsDetail?.video?.id,
      selectedReportDetail,
      selectedReportKey,
    ]
  );

  const confirmModerationAction = useCallback(
    (action) => {
      const contentType =
        selectedReportDetail?.contentType === 'COMMENT' ? 'COMMENT' : 'VIDEO';
      const cfg =
        contentType === 'COMMENT'
          ? COMMENT_MODERATION_CONFIRM[action]
          : MODERATION_CONFIRM[action];
      if (!cfg || applyingModerationAction) return;
      modal.confirm({
        title: cfg.title,
        content: cfg.content,
        okText: cfg.okText,
        cancelText: 'Cancelar',
        okButtonProps: { danger: cfg.danger },
        onOk: () => applyModerationAction(action),
      });
    },
    [applyModerationAction, applyingModerationAction, modal, selectedReportDetail]
  );

  const applyUnbanUser = useCallback(async () => {
    if (unbanInFlightRef.current || unbanningUser) return;

    const contentType =
      selectedReportDetail?.contentType === 'COMMENT' ? 'COMMENT' : 'VIDEO';
    if (contentType !== 'VIDEO') return;

    const clientId = selectedReportDetail?.video?.uploadedBy?.id;
    const parsed = parseReportSelectionKey(selectedReportKey);
    const reportId =
      parsed.id ??
      selectedReportDetail?.report?.id ??
      selectedReportDetail?.id;
    if (clientId == null || reportId == null) return;

    unbanInFlightRef.current = true;
    setUnbanningUser(true);
    try {
      await unbanClient(clientId);
      message.success('El usuario fue desbaneado correctamente.');
      try {
        await refreshReportDetail(reportId, 'VIDEO');
      } catch {
        // No actualizar isBanned en local si el refetch falla.
      }
    } catch (err) {
      message.error(getUnbanErrorMessage(err));
    } finally {
      unbanInFlightRef.current = false;
      setUnbanningUser(false);
    }
  }, [
    message,
    refreshReportDetail,
    selectedReportDetail,
    selectedReportKey,
    unbanningUser,
  ]);

  const confirmUnbanUser = useCallback(() => {
    if (unbanInFlightRef.current || unbanningUser) return;
    if (selectedReportDetail?.video?.uploadedBy?.isBanned !== true) return;
    modal.confirm({
      title: 'Desbanear usuario',
      content: '¿Deseas permitir nuevamente el acceso de este usuario?',
      okText: 'Desbanear usuario',
      cancelText: 'Cancelar',
      onOk: () => applyUnbanUser(),
    });
  }, [applyUnbanUser, modal, selectedReportDetail, unbanningUser]);

  const gerAIReport = useCallback(
    async (record) => {
      if (!canViewAiVerdict) return;
      if (!record.id) {
        message.warning('Este video no tiene información para consultar el dictamen de la IA');
        return;
      }
      setLoadingIA(true);
      const hideLoading = message.loading('Consultando dictamen de la IA…', 0);
      try {
        let aiReport = record.aiReport ?? undefined;
        if (!aiReport) {
          const evaluation = await getEvaluationById(record.id);
          aiReport = evaluation ?? undefined;
          setVideos(prev =>
            prev.map(item => (item.id === record.id ? { ...item, aiReport } : item))
          );
        }
        setEvaluation(aiReport);
      } catch {
        message.error(
          'No se pudo obtener dictamen de la IA.'
        );
      } finally {
        hideLoading();
        setLoadingIA(null);
      }
    },
    [message, canViewAiVerdict]
  );

  const toggleExpand = useCallback(async (record) => {
    if (!canViewScores) return;
    const key = record.id;
    if (expandedRowKeys.includes(key)) {
      setExpandedRowKeys([]);
      return;
    }
    const hideLoading = message.loading('Consultando puntuaciones…', 0);
    try {
      setLoadingRowKey(key);
      if (!record.winners) {
        const items = await getScoresById(key);
        const winners = items ?? undefined;
        setVideos(prev =>
          prev.map(item => (item.id === key ? { ...item, winners } : item))
        );
      }
      setExpandedRowKeys([key]);
    } catch {
      message.error('No se pudieron cargar los videos');
    } finally {
      hideLoading();
      setLoadingRowKey(null);
    }
  }, [expandedRowKeys, message, canViewScores]);

  const expandedRowRender = (record) => {
      if (loadingRowKey === record.id) {
        return <div style={{ textAlign: 'center' }}>Cargando...</div>;
      }
  
      if (!record.winners?.length) {
        return <div style={{ textAlign: 'center' }}>Sin videos</div>;
      }
  
      const sortedData = [...record.winners].sort(
        (a, b) => a.position - b.position || b.globalScore - a.globalScore
      );
  
      const judgesMap = new Map();
  
      sortedData.forEach(video => {
        video.evaluations?.forEach(ev => {
          judgesMap.set(ev.judge.id, ev.judge);
        });
      });
  
      const judges = Array.from(judgesMap.values());
  
      const judgeColumns = judges.map(judge => ({
        title: `Juez ${judge.firstName} ${judge.lastName}`,
        align: 'center',
        render: (_, row) => {
          const evaluation = row.evaluations?.find(
            ev => ev.judge.id === judge.id
          );
          return Number(evaluation?.judgeScore).toFixed(3) ?? '-';
        }
      }));
  
      return (
        <Table
          className='winners-table'
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content' }}
          dataSource={sortedData}
          columns={[
            {
              title: 'Participante',
              render: (_, r) =>
                (`${r.participant?.firstName ?? ''} ${r.participant?.lastName ?? ''} ${r.participant?.secondLastName ?? ''}`.trim()) || r.playerName
            },
            ...judgeColumns,
            {
              title: 'Promedio jueces',
              dataIndex: 'totalScore',
              align: 'center',
              render: (_, r) =>
                (Number(_ || 0)).toFixed(3)
            },
            {
              title: 'IA',
              dataIndex: 'aiTotalScore',
              align: 'center',
              render: (_, r) =>
                (Number(_ || 0) * 10).toFixed(3)
            },
            {
              title: 'Porcentaje Juez Voto',
              align: 'center',
              render: (_, r) =>
                r?.competition?.juryVote != null
                  ? Number(r.competition.juryVote).toFixed(3)
                  : '-',
            },
            {
              title: 'Total',
              dataIndex: 'juryScore',
              align: 'center',
              render: (_, r) =>
                (Number(_ || 0)).toFixed(3)
            },
            {
              title: 'Total Jurado',
              dataIndex: 'juryaivoteScore',
              align: 'center',
              render: (val) =>
                val != null ? Number(val).toFixed(3) : '-',
            },
            {
              title: 'Puntaje votaciones',
              dataIndex: 'publicScore',
              align: 'center',
              render: (_, r) =>
                (Number(_ || 0)).toFixed(3)
            },
            {
              title: 'Total Votos',
              dataIndex: 'totalVotes',
              align: 'center',
              render: (val) => (val != null ? Number(val) : '-'),
            },
            {
              title: 'Evaluación Final',
              dataIndex: 'globalScore',
              align: 'center',
              render: (_, r) =>
                (Number(_ || 0)).toFixed(3)
            },
          ]}
        />
      );
    };

  const columns = useMemo(
    () => [
      {
        title: 'Video',
        key: 'preview',
        width: 90,
        render: (_, record) => {
          const canPlay = Boolean(record.urlVideo);
          return (
            <div
              role="button"
              tabIndex={canPlay ? 0 : -1}
              aria-label={
                canPlay ? 'Reproducir vista previa del video' : 'Video sin URL para reproducir'
              }
              onClick={() => openVideoPreview(record)}
              onKeyDown={(e) => {
                if (!canPlay) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openVideoPreview(record);
                }
              }}
              style={{
                position: 'relative',
                width: 72,
                height: 48,
                cursor: canPlay ? 'pointer' : 'not-allowed',
                borderRadius: 4,
                overflow: 'hidden',
                outline: 'none',
              }}
            >
              {record.urlThumbnail ? (
                <img
                  src={record.urlThumbnail}
                  alt=""
                  style={{
                    width: 72,
                    height: 48,
                    objectFit: 'cover',
                    display: 'block',
                    opacity: canPlay ? 1 : 0.5,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 72,
                    height: 48,
                    background: '#1a1a2e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <VideoCameraOutlined style={{ color: '#fff', fontSize: 20 }} />
                </div>
              )}
              {canPlay && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.35)',
                    pointerEvents: 'none',
                  }}
                  aria-hidden
                >
                  <PlayCircleOutlined
                    style={{
                      fontSize: 26,
                      color: '#fff',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                    }}
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: 'Título',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
      },
      {
        title: 'Participante',
        dataIndex: 'participantName',
        key: 'participantName',
        ellipsis: true,
      },
      {
        title: 'Competencia',
        dataIndex: 'competition',
        key: 'competition',
        ellipsis: true,
      },
      {
        title: 'Estado Competencia',
        dataIndex: 'statusCompetition',
        key: 'statusCompetition',
        width: 130,
        render: (val) =>
           (
            <Tag color={val === 'En Curso' ? 'warning' : 'success'}>
              {val ?? '-'}
            </Tag>
          )
      },
      {
        title: 'Modalidad',
        dataIndex: 'modality',
        key: 'modality',
        ellipsis: true,
      },
      {
        title: 'Región',
        dataIndex: 'region',
        key: 'region',
        width: 140,
        ellipsis: true,
        render: (val) => val ?? '-',
      },
      {
        title: 'Estado',
        dataIndex: 'state',
        key: 'state',
        width: 140,
        ellipsis: true,
        render: (val) => val ?? '-',
      },
      {
        title: 'Ciudad',
        dataIndex: 'city',
        key: 'city',
        width: 120,
        ellipsis: true,
        render: (val) => val ?? '-',
      },
      {
        title: 'Finalista',
        dataIndex: 'isFinalist',
        key: 'isFinalist',
        width: 130,
        render: (val) =>
          val === 'Finalista' || val === 'finalistas' ? (
            <Tag color="success">Finalista</Tag>
          ) : (
            <Tag color="error">No finalista</Tag>
          ),
      },
      {
        title: 'Periodo',
        dataIndex: 'period',
        key: 'period',
        width: 110,
      },
      ...(canViewCompetitions
        ? [
            {
              title: 'Reportes',
              key: 'reports',
              width: 130,
              render: (_, record) => {
                const count = getTotalReportCount(record);
                if (count <= 0) {
                  return <Text type="secondary">Sin reportes</Text>;
                }
                return (
                  <Tag color="warning">
                    {count === 1 ? '1 reporte' : `${count} reportes`}
                  </Tag>
                );
              },
            },
          ]
        : []),
      {
        title: 'Fecha de carga',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        render: (val) =>
          val ? dayjs(val).format('DD/MM/YYYY HH:mm') : '-',
      },
      {
        title: 'Tamaño',
        dataIndex: 'fileSize',
        key: 'fileSize',
        width: 100,
        render: (val) => (val != null ? `${Number(val).toFixed(1)} MB` : '-'),
      },
      {
        title: 'Posición',
        dataIndex: 'position',
        key: 'position',
        width: 110,
        align: 'center',
        sorter: true,
        sortOrder: sorter.field === 'position' ? sorter.order : undefined,
        render: (val) => (val != null ? `${val}°` : '-'),
      },
      {
        title: 'Acción',
        key: 'action',
        width: 120,
        fixed: 'right',
        render: (_, record) => (
          <div>
            <Tooltip title="Descargar video">
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
                disabled={!record.urlVideo}
                loading={downloadingUrl === record.urlVideo}
              />
            </Tooltip>
            {canViewScores && (
            <Tooltip title="Ver puntuación">
              <Button
              type="link"
              disabled={loadingRowKey}
              loading={loadingRowKey}
              icon={expandedRowKeys.includes(record.id) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => toggleExpand(record)}
              />
            </Tooltip>
            )}
            {canViewAiVerdict && (
            <Tooltip title="Ver Dictamen IA">
              <Button
                type="link"
                icon={<ProfileOutlined />}
                onClick={() => gerAIReport(record)}
                disabled={!record.id}
                loading={loadingIA}
              />
            </Tooltip>
            )}
            {canViewCompetitions && getTotalReportCount(record) > 0 && (
              <Tooltip title="Ver reportes">
                <Button
                  type="link"
                  icon={<WarningOutlined />}
                  onClick={() => openVideoReports(record)}
                  loading={loadingReportsVideoId === record.id}
                  disabled={loadingReportsVideoId != null}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [
      sorter,
      handleDownload,
      downloadingUrl,
      openVideoPreview,
      expandedRowKeys,
      toggleExpand,
      loadingRowKey,
      gerAIReport,
      loadingIA,
      openVideoReports,
      loadingReportsVideoId,
      canViewCompetitions,
      canViewScores,
      canViewAiVerdict,
    ]
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Encabezado */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Descarga de Videos de Competencia
          </Title>
          <Text type="secondary">
            Visualiza y descarga los videos subidos en una competencia,
            filtrándolos entre finalistas y no finalistas.
          </Text>
        </div>
        <Space>
          {canViewCompetitions && (
          <Button
            icon={<EyeInvisibleOutlined />}
            onClick={() => setHiddenUsersOpen(true)}
          >
            Usuarios bloqueados
            {hiddenUsersPendingCount > 0 ? (
              <Tag
                color="warning"
                style={{ marginInlineStart: 8, marginInlineEnd: 0 }}
              >
                {hiddenUsersPendingCount === 1
                  ? '1 pendiente'
                  : `${hiddenUsersPendingCount} pendientes`}
              </Tag>
            ) : null}
          </Button>
          )}
          <Tooltip title="Selecciona al menos un filtro (competencia, modalidad, periodo, etc.) y pulsa Aplicar filtros para ver los videos. Luego puedes descargar o previsualizar cada uno.">
            <Button icon={<QuestionCircleOutlined />}>Guía de uso</Button>
          </Tooltip>
        </Space>
      </div>

      {/* Bloque de filtros */}
      <Card
        title={
          <Space>
            <FilterOutlined />
            Filtros
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Form
          form={form}
          layout="vertical"
          data-form-revision={filterFormRevision}
          onValuesChange={() => bumpFilterFormRevision()}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="competitionId" label="Competencia">
                <Select
                  showSearch
                  placeholder="Todas las competencias"
                  loading={loadingCatalogs}
                  optionFilterProp="label"
                  options={competitions.map((c) => ({
                    value: c.id,
                    label: c.description ?? c.name ?? c.title ?? `Competencia ${c.id}`,
                  }))}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="modalityId" label="Modalidad">
                <Select
                  showSearch
                  placeholder="Todas las modalidades"
                  loading={loadingCatalogs}
                  optionFilterProp="label"
                  options={modalities.map((m) => ({
                    value: m.id,
                    label: m.name ?? m.description,
                  }))}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="periodId" label="Periodo">
                <Select
                  showSearch
                  placeholder="Todos los periodos"
                  loading={loadingCatalogs}
                  optionFilterProp="label"
                  options={periods.map((p) => ({
                    value: p.id,
                    label: p.name ?? p.description,
                  }))}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="isFinalist" label="Finalista">
                <Select
                  placeholder="Todos"
                  options={FINALIST_OPTIONS}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="statusCompetition" label="Estatus Competencia">
                <Select
                  placeholder="Todos"
                  options={STATUS_OPTIONS}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="regionId" label="Región">
                <Select
                  showSearch
                  placeholder="Todas las regiones"
                  loading={loadingCatalogs}
                  optionFilterProp="label"
                  options={regions.map((r) => ({
                    value: r.id,
                    label: r.name ?? r.description,
                  }))}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="stateId" label="Estado">
                <Select
                  showSearch
                  placeholder={
                    regionId ? 'Todos los estados' : 'Primero elige región'
                  }
                  loading={loadingStatesByRegion}
                  optionFilterProp="label"
                  options={statesInRegion.map((s) => ({
                    value: s.id,
                    label: s.description ?? s.name,
                  }))}
                  disabled={!regionId}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="cityId" label="Ciudad">
                <Select
                  showSearch
                  placeholder={
                    stateId ? 'Todas las ciudades' : 'Primero elige estado'
                  }
                  optionFilterProp="label"
                  options={cities.map((c) => ({
                    value: c.id,
                    label: c.name ?? c.description,
                  }))}
                  disabled={!stateId}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="participantSearch" label="Participante">
                <Input
                  placeholder="Buscar participante..."
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="dateRange" label="Periodo (rango de fechas)">
                <RangePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder={['Desde', 'Hasta']}
                />
              </Form.Item>
            </Col>

            {canViewCompetitions && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="onlyReported"
                label="Reportes"
                valuePropName="checked"
              >
                <Checkbox>Solo videos reportados</Checkbox>
              </Form.Item>
            </Col>
            )}
          </Row>

          <Divider style={{ margin: '8px 0 16px' }} />

          <Row justify="end">
            <Space>
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
              >
                Limpiar filtros
              </Button>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleApplyFilters}
                disabled={!hasAtLeastOneFilter(form.getFieldsValue())}
                loading={loading}
              >
                Aplicar filtros
              </Button>
            </Space>
          </Row>
        </Form>
      </Card>

      {/* Tabla de resultados */}
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            Videos{activeFilters ? ` (${total})` : ''}
          </Title>
          {!activeFilters && (
            <Text type="secondary">
              Elige al menos un filtro y pulsa Aplicar filtros para ver los videos
            </Text>
          )}
        </div>

        <Table
          rowKey="id"
          dataSource={videos}
          columns={columns}
          loading={loading}
          scroll={{ x: 1600 }}
          expandable={{
            expandedRowKeys,
            expandedRowRender,
            expandIcon: () => null,
            showExpandColumn: false,
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            showTotal: (t, range) =>
              `${range[0]}-${range[1]} de ${t} videos`,
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: activeFilters
              ? 'No se encontraron videos con los filtros aplicados'
              : 'Aplica los filtros para ver los videos',
          }}
        />
      </Card>

      <Modal
        title={videoPreview?.title}
        open={Boolean(videoPreview)}
        onCancel={() => setVideoPreview(null)}
        footer={null}
        width={880}
        centered
        destroyOnClose
      >
        {videoPreview && (
          <video
            key={videoPreview.url}
            controls
            playsInline
            preload="metadata"
            style={{
              width: '100%',
              maxHeight: '75vh',
              background: '#000',
              borderRadius: 4,
            }}
            src={videoPreview.url}
          >
            Tu navegador no puede reproducir este video.
          </video>
        )}
      </Modal>

      <Modal
        title={selectedReportDetail ? 'Detalle del reporte' : 'Reportes del video'}
        open={Boolean(reportsDetail)}
        onCancel={() => {
          if (applyingModerationAction || unbanningUser) return;
          closeReportsModal();
        }}
        maskClosable={!applyingModerationAction && !unbanningUser}
        closable={!applyingModerationAction && !unbanningUser}
        footer={
          selectedReportDetail ? (
            <Space>
              {!closeReportsModalOnBack && reportsDetail ? (
                <Button
                  onClick={backToReportsList}
                  disabled={applyingModerationAction || unbanningUser}
                >
                  Volver a reportes
                </Button>
              ) : null}
              <Button
                onClick={closeReportsModal}
                disabled={applyingModerationAction || unbanningUser}
              >
                Cerrar
              </Button>
            </Space>
          ) : null
        }
        width={960}
        centered
        destroyOnClose
      >
        {selectedReportDetail ? (
          <ReportDetailContent
            detail={selectedReportDetail}
            canModerate={canModerate}
            applyingModerationAction={applyingModerationAction}
            unbanningUser={unbanningUser}
            onPreviewVideo={openVideoPreview}
            onModerationAction={confirmModerationAction}
            onUnbanUser={confirmUnbanUser}
          />
        ) : reportsDetail ? (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={8} md={6}>
                {reportsDetail.video?.urlThumbnail ? (
                  <img
                    src={reportsDetail.video.urlThumbnail}
                    alt=""
                    style={{
                      width: '100%',
                      maxWidth: 160,
                      borderRadius: 4,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 120,
                      height: 80,
                      background: '#1a1a2e',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 4,
                    }}
                  >
                    <VideoCameraOutlined style={{ color: '#fff', fontSize: 24 }} />
                  </div>
                )}
              </Col>
              <Col xs={24} sm={16} md={18}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary">Título: </Text>
                    <Text strong>{reportsDetail.video?.title || '-'}</Text>
                  </div>
                  <div>
                    <Text type="secondary">Participante: </Text>
                    <Text>
                      {reportsDetail.video?.participantName ||
                        reportsDetail.video?.playerName ||
                        '-'}
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary">Competencia: </Text>
                    <Text>
                      {formatCompetitionLabel(reportsDetail.video?.competition)}
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary">Fecha de carga: </Text>
                    <Text>
                      {reportsDetail.video?.createdAt
                        ? dayjs(reportsDetail.video.createdAt).format(
                            'DD/MM/YYYY HH:mm'
                          )
                        : '-'}
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary">Total de reportes: </Text>
                    <Text strong>
                      {(reportsDetail.total ?? 0) +
                        (reportsDetail.commentReportTotal ?? 0)}
                    </Text>
                  </div>
                  {reportsDetail.video?.urlVideo && (
                    <Button
                      type="link"
                      icon={<PlayCircleOutlined />}
                      style={{ paddingLeft: 0 }}
                      onClick={() =>
                        openVideoPreview({
                          urlVideo: reportsDetail.video.urlVideo,
                          title: reportsDetail.video.title || 'Vista previa',
                        })
                      }
                    >
                      Ver video
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>

            <Title level={5} style={{ marginTop: 8, marginBottom: 12 }}>
              Reportes del video ({reportsDetail.total ?? 0})
            </Title>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={reportsDetail.reports ?? []}
              locale={{
                emptyText: 'No hay reportes directos sobre el video.',
              }}
              columns={[
                {
                  title: 'Tipo',
                  key: 'contentType',
                  width: 120,
                  render: (_, row) =>
                    contentTypeTag(row.contentType || 'VIDEO'),
                },
                {
                  title: 'Fecha',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  width: 150,
                  render: (val) =>
                    val ? dayjs(val).format('DD/MM/YYYY HH:mm') : '-',
                },
                {
                  title: 'Reportado por',
                  key: 'reportedBy',
                  ellipsis: true,
                  render: (_, row) => formatReporterName(row.reportedBy),
                },
                {
                  title: 'Motivo',
                  key: 'reason',
                  ellipsis: true,
                  render: (_, row) => (
                    <div>
                      <div>{row.reason?.name || '-'}</div>
                      {row.reason?.description ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {row.reason.description}
                        </Text>
                      ) : null}
                    </div>
                  ),
                },
                {
                  title: 'Estado',
                  dataIndex: 'status',
                  key: 'status',
                  width: 130,
                  render: (status) => reportStatusTag(status),
                },
                {
                  title: 'Acción',
                  key: 'action',
                  width: 120,
                  render: (_, row) => {
                    const key = reportSelectionKey('VIDEO', row.id);
                    return (
                      <Button
                        type="link"
                        onClick={() =>
                          openReportDetail(row.id, row.contentType || 'VIDEO')
                        }
                        loading={loadingReportKey === key}
                        disabled={loadingReportKey != null}
                      >
                        Ver detalle
                      </Button>
                    );
                  },
                },
              ]}
            />

            <Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
              Reportes de comentarios ({reportsDetail.commentReportTotal ?? 0})
            </Title>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={reportsDetail.commentReports ?? []}
              locale={{
                emptyText: 'No hay comentarios reportados en este video.',
              }}
              columns={[
                {
                  title: 'Tipo',
                  key: 'contentType',
                  width: 130,
                  render: () => contentTypeTag('COMMENT'),
                },
                {
                  title: 'Comentario',
                  key: 'text',
                  ellipsis: true,
                  render: (_, row) => row.text || '-',
                },
                {
                  title: 'Publicado por',
                  key: 'commentOwner',
                  ellipsis: true,
                  render: (_, row) => formatReporterName(row.commentOwner),
                },
                {
                  title: 'Reportado por',
                  key: 'reportedBy',
                  ellipsis: true,
                  render: (_, row) => formatReporterName(row.reportedBy),
                },
                {
                  title: 'Motivo',
                  key: 'reason',
                  ellipsis: true,
                  render: (_, row) => row.reason?.name || '-',
                },
                {
                  title: 'Fecha',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  width: 150,
                  render: (val) =>
                    val ? dayjs(val).format('DD/MM/YYYY HH:mm') : '-',
                },
                {
                  title: 'Estado',
                  dataIndex: 'status',
                  key: 'status',
                  width: 130,
                  render: (status) => reportStatusTag(status),
                },
                {
                  title: 'Acción',
                  key: 'action',
                  width: 120,
                  render: (_, row) => {
                    const key = reportSelectionKey('COMMENT', row.id);
                    return (
                      <Button
                        type="link"
                        onClick={() =>
                          openReportDetail(row.id, 'COMMENT')
                        }
                        loading={loadingReportKey === key}
                        disabled={loadingReportKey != null}
                      >
                        Ver detalle
                      </Button>
                    );
                  },
                },
              ]}
            />
          </>
        ) : null}
      </Modal>

      <Modal
        title={`${
            evaluation?.competition?.modality?.name ||
            evaluation?.competition?.modality?.description ||
            '-'
          } 
          Región ${
            evaluation?.competition?.regions?.length > 0
              ? evaluation.competition.regions
                  .map(r => r.region?.description)
                  .filter(Boolean)
                  .join(', ')
              : evaluation?.region?.description || '-'
          }
          Competición ${
            evaluation?.competition?.gender?.name ||
            evaluation?.competition?.gender?.description ||
            '-'
          }`
        }
        open={Boolean(evaluation)}
        onCancel={() => setEvaluation(null)}
        footer={null}
        width={1200}
        centered
        destroyOnClose
      >
        {evaluation && (
          <Card >
            <Row gutter={24}>
              <Col xs={24} md={10}>
                <video
                  src={resolveMedia(evaluation?.urlVideo)}
                  controls
                  style={{ width: '100%', borderRadius: 8 }}
                />
    
                <div style={{ marginTop: 16 }}>
                  <strong>{evaluation?.folio || '—'}</strong>
                  <br />
                  Usuario: {evaluation?.playerName || '—'}
                </div>
              </Col>
    
              <Col xs={24} md={14}>
                <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
                  <strong>Análisis del video</strong>
                  <div>{evaluation?.urlVideo?.split('/').pop() || '—'}</div>
                </div>
    
                <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
                  <strong>Tipo de jugada</strong>
                  <div>[{evaluation?.aiCategory || '—'}]</div>
                </div>
    
                <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
                  <strong>Descripción</strong>
                  <div style={{ textAlign: 'justify' }}>
                    {evaluation?.aiContext || '—'}
                  </div>
                </div>
    
                <Title
                  level={5}
                  style={{
                    color: '#BA955E',
                    marginTop: 24,
                    marginBottom: 16,
                    fontWeight: 600,
                  }}
                >
                  DICTAMEN DEL JURADO
                </Title>
    
                <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
                  <strong>Justificación técnica:</strong>
                  <div style={{ textAlign: 'justify' }}>
                    {evaluation?.aiJustification || '—'}
                  </div>
                </div>
    
                <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 12, marginBottom: 12 }}>
                  <strong>Análisis del contexto:</strong>
                  <div style={{ textAlign: 'justify' }}>
                    {evaluation?.aiContextAnalysis || '—'}
                  </div>
                </div>
    
                <div>
                  <strong>Conclusión:</strong>
                  <div style={{ textAlign: 'justify' }}>
                    {evaluation?.aiConclusion || '—'}
                  </div>
                </div>
              </Col>
              {/* <Col span={12}> <a href={GuiaPDF} target="_blank" rel="noopener noreferrer" style={{ color: '#BA955E' }} > Consultar criterios de evaluación (PDF) </a> </Col> */}
            </Row>
          </Card>
        )}
      </Modal>

      {canViewCompetitions && (
      <HiddenUsersModal
        open={hiddenUsersOpen}
        onClose={() => setHiddenUsersOpen(false)}
        onPreviewVideo={openVideoPreview}
        onGlobalPendingCountChange={setHiddenUsersPendingCount}
      />
      )}
    </div>
  );
};

export default CompetitionVideosPage;
